// src/components/Navbar.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { FiAlignJustify, FiSearch } from "react-icons/fi";
import { RiMoonFill, RiSunFill } from "react-icons/ri";
import { useAuth } from "../../views/auth/AuthContext";
import Modal from "components/ui/Modal";

export default function Navbar({ onOpenSidenav, brandText = "Dashboard" }) {
  const nav = useNavigate();
  const { user, signout } = useAuth();

  const [dark, setDark] = React.useState(
    () => localStorage.getItem("theme") === "dark"
  );

  // logout confirm modal state
  const [confirmLogoutOpen, setConfirmLogoutOpen] = React.useState(false);
  const [loggingOut, setLoggingOut] = React.useState(false);

  // 👇 ref for profile dropdown <details>
  const profileRef = React.useRef(null);

  // theme toggle side-effects
  React.useEffect(() => {
    if (dark) {
      document.body.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.body.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [dark]);

  // 👇 close profile menu when clicking outside
  React.useEffect(() => {
    function handleClickOutside(e) {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        profileRef.current.removeAttribute("open");
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const openConfirmLogout = () => setConfirmLogoutOpen(true);

  const closeConfirmLogout = () => {
    if (loggingOut) return;
    setConfirmLogoutOpen(false);
  };

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      await signout();
      nav("/signin", { replace: true });
    } finally {
      setLoggingOut(false);
      setConfirmLogoutOpen(false);
    }
  };

  return (
    <>
      <nav className="sticky top-4 z-40 flex items-center justify-between rounded-xl bg-white/10 p-2 backdrop-blur-xl dark:bg-[#0b14374d]">
        <div className="ml-[6px]">
          <div className="h-6 w-[224px] pt-1 text-sm text-navy-700 dark:text-white">
            Pages <span className="mx-1">/</span>{" "}
            <span className="capitalize">{brandText}</span>
          </div>
          <p className="text-[33px] font-bold capitalize text-navy-700 dark:text-white">
            {brandText}
          </p>
        </div>

        <div className="relative mt-[3px] flex h-[61px] w-[355px] flex-grow items-center gap-2 rounded-full bg-white px-2 py-2 shadow-xl dark:!bg-navy-800 md:w-[365px] md:flex-grow-0 xl:w-[365px]">
          {/* search */}
          <div className="flex h-full flex-1 items-center rounded-full bg-lightPrimary text-navy-700 dark:bg-navy-900 dark:text-white">
            <span className="pl-3 pr-2 text-xl">
              <FiSearch className="h-4 w-4 text-gray-400 dark:text-white" />
            </span>
            <input
              type="text"
              placeholder="Search..."
              className="block h-full w-full rounded-full bg-lightPrimary text-sm font-medium text-navy-700 outline-none placeholder:!text-gray-400 dark:bg-navy-900 dark:text-white dark:placeholder:!text-white"
            />
          </div>

          {/* open sidebar (mobile) */}
          <button
            type="button"
            onClick={() => onOpenSidenav?.()}
            className="text-gray-600 dark:text-white xl:hidden"
            aria-label="Open sidebar"
          >
            <FiAlignJustify className="h-5 w-5" />
          </button>

          {/* theme toggle */}
          <button
            type="button"
            onClick={() => setDark((v) => !v)}
            className="text-gray-600 dark:text-white"
            aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
          >
            {dark ? (
              <RiSunFill className="h-5 w-5" />
            ) : (
              <RiMoonFill className="h-5 w-5" />
            )}
          </button>

          {/* profile quick menu */}
          <div className="relative">
            <details ref={profileRef} className="group">
              <summary
                title={user?.name || "User"}
                className="cursor-pointer select-none list-none rounded-full bg-lightPrimary px-3 py-1 text-sm dark:bg-navy-900 dark:text-white"
              >
                {(() => {
                  if (!user?.name) return "U";
                  const parts = user.name.trim().split(" ");
                  const first = parts[0]?.charAt(0).toUpperCase() || "";
                  const last = parts[1]?.charAt(0).toUpperCase() || "";
                  return `${first} ${last}`.trim();
                })()}
              </summary>

              <div className="absolute right-0 mt-2 w-48 rounded-2xl bg-white p-2 shadow-xl dark:bg-navy-700">
                <button
                  type="button"
                  className="w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-gray-100 dark:text-white dark:hover:bg-white/10"
                  onClick={() => {
                    profileRef.current?.removeAttribute("open"); // CLOSE DROPDOWN
                    nav("/admin/profile");
                  }}
                >
                  Profile Settings
                </button>
                <button
                  type="button"
                  className="mt-1 w-full rounded-lg bg-gradient-to-r from-[#ef145d] to-[#e3955e] px-3 py-2 text-left text-sm font-medium text-white hover:opacity-95 dark:hover:bg-white/10"
                  onClick={() => {
                    profileRef.current?.removeAttribute("open"); // CLOSE DROPDOWN
                    openConfirmLogout();
                  }}
                >
                  Log Out
                </button>
              </div>
            </details>
          </div>
        </div>
      </nav>

      {/* Logout Confirmation Modal */}
      <Modal
        open={confirmLogoutOpen}
        onClose={loggingOut ? undefined : closeConfirmLogout}
        title="Log out from Stado Payroll?"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-white/80">
            Are you sure you want to{" "}
            <span className="font-medium">log out of your account</span>? Any
            unsaved changes may be lost.
          </p>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              disabled={loggingOut}
              onClick={closeConfirmLogout}
              className="hover:bg-black rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-60 dark:border-white/10 dark:text-white"
            >
              Cancel
            </button>
            <button
              onClick={handleLogout}
              disabled={loggingOut}
              className="focus:ring-rose-500 rounded-lg bg-gradient-to-r from-[#ef145d] to-[#e3955e] px-5 py-2 text-sm text-white hover:opacity-95 focus:outline-none focus:ring-2 disabled:opacity-60"
            >
              {loggingOut ? "Logging out…" : "Logout"}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
